import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import CloseIcon from '@mui/icons-material/Close'
import PrintIcon from '@mui/icons-material/Print'
import ContrastIcon from '@mui/icons-material/Contrast'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import AirplayIcon from '@mui/icons-material/Airplay'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import type { RootState } from '../store'
import { alternarModoTema } from '../store/tema-slice'
import { onAbrirConfigDispositivo } from '../lib/tablet-eventos'
import { urlTrocarPontoTotem } from '../lib/url-pdv'
import { soltarUrlDoAparelho } from '../lib/vinculo-aparelho'
import { useGetLojaAtualQuery, useValidarPinTotemMutation } from '../api'
import { getLocalImpressaoTotem, setLocalImpressaoTotem } from '../lib/totem-config'
import { imprimeNoAparelho, temImpressoraNoAparelho } from '../lib/impressao-totem'
import NumericKeypad from '../pages/totem/checkout/components/numeric-keypad'
import { SelectLocalImpressao } from './select-local-impressao'
import { PreferenciasAparelhoTotem } from './totem/preferencias-aparelho'
import { AtivacaoTefTotem } from './totem/ativacao-tef'

type Fase = 'pin' | 'config'

export function ConfigTotem({
    semBotao = false,
    obrigatorio = false,
    onConfigurado,
}: {
    semBotao?: boolean
    obrigatorio?: boolean
    onConfigurado?: () => void
} = {}): JSX.Element | null {
    const dispatch = useDispatch()
    const modoTema = useSelector((s: RootState) => s.tema.mode)
    const { data: loja } = useGetLojaAtualQuery()
    const [validarPin, statusPin] = useValidarPinTotemMutation()
    const [aberto, setAberto] = useState(false)
    const [fase, setFase] = useState<Fase>('pin')
    const [pin, setPin] = useState('')
    const [erro, setErro] = useState<string | null>(null)
    const [localImpressao, setLocalImpressao] = useState('')
    const [confirmandoTroca, setConfirmandoTroca] = useState(false)
    const [impressoraDoAparelho, setImpressoraDoAparelho] = useState<boolean | null>(null)
    const noAparelho = imprimeNoAparelho()
    const podeFechar = !obrigatorio || (noAparelho ? impressoraDoAparelho === true : !!localImpressao.trim())

    useEffect(() => onAbrirConfigDispositivo(() => abrir()), [])

    useEffect(() => {
        if (!noAparelho || !aberto) return

        let vivo = true
        void temImpressoraNoAparelho().then((tem) => { if (vivo) setImpressoraDoAparelho(tem) })

        return () => { vivo = false }
    }, [noAparelho, aberto])

    useEffect(() => {
        if (obrigatorio) abrir()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obrigatorio])

    if (loja?.identificador.tipo !== 'TOTEM') return null

    function abrir(): void {
        setErro(null)
        setPin('')
        setFase('pin')
        setConfirmandoTroca(false)
        setLocalImpressao(getLocalImpressaoTotem())
        setAberto(true)
    }

    function fechar(): void {
        if (!podeFechar) return

        setAberto(false)
        onConfigurado?.()
    }

    async function confirmarPin(): Promise<void> {
        setErro(null)

        try {
            const r = await validarPin({ pin: pin.trim() }).unwrap()

            if (r.ok) setFase('config')
            else setErro('PIN incorreto.')
        } catch {
            setErro('Não foi possível validar o PIN. Tente novamente.')
        }
    }

    return (
        <>
            {!semBotao && (
                <Button
                    size="small"
                    color="inherit"
                    startIcon={<SettingsIcon fontSize="small" />}
                    onClick={abrir}
                    sx={{ opacity: 0.85, textTransform: 'none', mt: 0.5 }}
                >
                    Configurar totem
                </Button>
            )}

            <Dialog
                open={aberto}
                onClose={(_e, motivo) => { if (motivo !== 'backdropClick') fechar() }}
                fullWidth
                maxWidth={fase === 'pin' ? 'xs' : 'sm'}
            >
                <DialogTitle sx={{ p: 2 }}>
                    <Stack direction="row" sx={{ alignItems: 'start', justifyContent: 'space-between' }}>
                        <Typography variant="h6">
                            {fase === 'pin' ? 'Configuração do totem' : 'Configurações'}
                        </Typography>

                        {podeFechar && (
                            <IconButton aria-label="Fechar" onClick={fechar} sx={{ p: 0 }}>
                                <CloseIcon />
                            </IconButton>
                        )}
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ p: 2 }}>
                    {obrigatorio && (
                        <Alert severity={podeFechar ? 'success' : 'warning'} sx={{ mb: 2 }}>
                            {podeFechar
                                ? (noAparelho
                                    ? 'Impressora conectada a este totem. Feche esta janela para liberar os pedidos.'
                                    : 'Impressora salva neste totem. Feche esta janela para liberar os pedidos.')
                                : (noAparelho
                                    ? 'Conecte a impressora ao totem e ligue-a para liberar os pedidos. Sem ela, o cliente não recebe a senha para retirar o pedido.'
                                    : 'Escolha a impressora deste totem para liberar os pedidos. Sem ela, o cliente não recebe a senha para retirar o pedido.')}
                        </Alert>
                    )}

                    {erro && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {erro}
                        </Alert>
                    )}

                    {fase === 'pin' ? (
                        <Stack spacing={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                {pin ? (
                                    <Typography variant="h3">
                                        {'•'.repeat(pin.length)}
                                    </Typography>
                                ) : (
                                    <Typography variant="h3" color="text.secondary">
                                        Digite o PIN
                                    </Typography>
                                )}
                            </Box>

                            <Box>
                                <NumericKeypad
                                    onDigito={(d) => setPin((p) => (p + d).slice(0, 8))}
                                    onApagar={() => setPin((p) => p.slice(0, -1))}
                                    onConfirmar={() => {
                                        if (pin.trim() && !statusPin.isLoading) void confirmarPin()
                                    }}
                                    apagarDesabilitado={!pin}
                                    confirmarDesabilitado={!pin.trim() || statusPin.isLoading}
                                />
                            </Box>
                        </Stack>
                    ) : (
                        <Stack spacing={2}>
                            <Stack spacing={1}>
                                <Stack spacing={1} direction="row" sx={{ alignItems: 'center' }}>
                                    <List sx={{ width: '100%', p: 0 }}>
                                        <ListItem sx={{ pl: 1, py: 0 }}>
                                            <ListItemIcon sx={{ minWidth: '48px' }}>
                                                <PrintIcon sx={{ fontSize: '30px' }} />
                                            </ListItemIcon>

                                            <ListItemText
                                                primary="Impressora"
                                                secondary="Onde saem a senha do pedido e o comprovante do cartão deste totem"
                                                slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                                            />
                                        </ListItem>
                                    </List>
                                </Stack>

                                {noAparelho ? (
                                    <Stack spacing={1} sx={{ pl: 1 }}>
                                        <Alert severity={impressoraDoAparelho === true ? 'success' : impressoraDoAparelho === false ? 'warning' : 'info'}>
                                            {impressoraDoAparelho === true && 'Impressora do totem conectada. Não há o que escolher neste aparelho.'}
                                            {impressoraDoAparelho === false && 'Nenhuma impressora encontrada neste totem. Confira o cabo USB e se ela está ligada.'}
                                            {impressoraDoAparelho === null && 'Procurando a impressora do totem...'}
                                        </Alert>

                                        <Button
                                            variant="outlined"
                                            color="inherit"
                                            onClick={() => {
                                                setImpressoraDoAparelho(null)
                                                void temImpressoraNoAparelho().then(setImpressoraDoAparelho)
                                            }}
                                        >
                                            Verificar de novo
                                        </Button>
                                    </Stack>
                                ) : (
                                    <SelectLocalImpressao
                                        pin={pin.trim()}
                                        valor={localImpressao}
                                        onChange={(valor) => {
                                            setLocalImpressao(valor)
                                            setLocalImpressaoTotem(valor)
                                        }}
                                        ajuda="Impressora acoplada a este totem."
                                    />
                                )}
                            </Stack>

                            <Stack spacing={1} sx={{ pt: 1 }}>
                                <Stack spacing={1}>
                                    <Stack spacing={1} direction="row" sx={{ alignItems: 'center' }}>
                                        <List sx={{ width: '100%', p: 0 }}>
                                            <ListItem sx={{ pl: 1, py: 0 }}>
                                                <ListItemIcon sx={{ minWidth: '48px' }}>
                                                    <ContrastIcon sx={{ fontSize: '30px' }} />
                                                </ListItemIcon>

                                                <ListItemText
                                                    primary="Tema do aplicativo"
                                                    secondary="Escolha entre tema claro ou escuro para o aplicativo do totem."
                                                    slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                                                />
                                            </ListItem>
                                        </List>
                                    </Stack>

                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        startIcon={modoTema === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                                        sx={{ textTransform: 'none' }}
                                        onClick={() => dispatch(alternarModoTema())}
                                    >
                                        {modoTema === 'dark' ? 'Usar tema claro' : 'Usar tema escuro'}
                                    </Button>
                                </Stack>

                                <PreferenciasAparelhoTotem />

                                <AtivacaoTefTotem />

                                {urlTrocarPontoTotem() && (
                                    <>
                                        <Stack spacing={1} direction="row" sx={{ alignItems: 'center', pt: 2 }}>
                                            <List sx={{ width: '100%', p: 0 }}>
                                                <ListItem sx={{ pl: 1, py: 0 }}>
                                                    <ListItemIcon sx={{ minWidth: '48px' }}>
                                                        <AirplayIcon sx={{ fontSize: '30px' }} />
                                                    </ListItemIcon>

                                                    <ListItemText
                                                        primary="Ponto de acesso"
                                                        secondary="Quando clicado, você pode trocar para outro ponto de acesso."
                                                        slotProps={{ primary: { sx: { fontWeight: 'bold' } } }}
                                                    />
                                                </ListItem>
                                            </List>
                                        </Stack>

                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            startIcon={<SwapHorizIcon />}
                                            sx={{ textTransform: 'none' }}
                                            onClick={() => setConfirmandoTroca(true)}
                                        >
                                            Trocar ponto de acesso
                                        </Button>

                                        {confirmandoTroca && (
                                            <Alert
                                                severity="warning"
                                                action={
                                                    <Button
                                                        size="small"
                                                        color="inherit"
                                                        onClick={() => void soltarUrlDoAparelho().then(() => { window.location.href = urlTrocarPontoTotem()! })}
                                                    >
                                                        Trocar
                                                    </Button>
                                                }
                                            >
                                                Este totem sai do ponto de acesso atual e volta a pedir um código de
                                                pareamento. Tenha o código novo em mãos antes de continuar.
                                            </Alert>
                                        )}
                                    </>
                                )}
                            </Stack>
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}
